import DefaultButton from '@/components/common/default/DefaultButton';
import useBackendUser from '@/hooks/useBackendUser';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import { Transition } from '@headlessui/react';
import { ChevronRight, Globe } from 'lucide-react';
import { useRouter } from 'next/router';
import { Fragment, useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import handleLanguageChange from '@/utilites/handle-language-change';
import { Locales } from '@/types/enums';
import { useTranslation } from 'next-i18next';
import languageConstants from '@/constants/language.constants';
import Image from 'next/image';
import clsx from 'clsx';

export default function HeaderUserDropdownLanguage({
  languageDropdownOpen,
  setLanguageDropdownOpen,
}: {
  languageDropdownOpen: boolean;
  setLanguageDropdownOpen: (open: boolean) => void;
}) {
  const { data: user } = useBackendUser();
  const router = useRouter();
  const { locale } = router;
  const queryClient = useQueryClient();
  const { translate } = useTypedTranslation<HeaderEnum>('header');
  const { t: tCommon } = useTranslation('common');
  const [doingLanguageChange, setDoingLanguageChange] = useState(false);

  // Ref for close timer
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Ref for dropdown to detect hover
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Function to start close timer
  const startCloseTimer = () => {
    // Clear any existing timer first
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    // Set new timer
    closeTimerRef.current = setTimeout(() => {
      setLanguageDropdownOpen(false);
    }, 6000);
  };

  // Function to cancel timer
  const cancelCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Close on unmount
  useEffect(() => {
    return () => {
      setLanguageDropdownOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to handle timer cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Effect to start/cancel timer when dropdown state changes
  useEffect(() => {
    if (languageDropdownOpen) {
      startCloseTimer();
    } else {
      cancelCloseTimer();
    }
  }, [languageDropdownOpen]);

  async function handleChangeLanguage(locale: Locales) {
    cancelCloseTimer(); // Cancel timer when changing language
    setLanguageDropdownOpen(false);
    setDoingLanguageChange(true);
    await handleLanguageChange(locale, tCommon, router, Boolean(user), queryClient);
    setDoingLanguageChange(false);
  }

  const languageOptions = [
    { locale: Locales.DE, name: translate(HeaderEnum['locales.german']) },
    { locale: Locales.EN, name: translate(HeaderEnum['locales.english']) },
    { locale: Locales.ES, name: translate(HeaderEnum['locales.spanish']) },
    { locale: Locales.FR, name: translate(HeaderEnum['locales.french']) },
    { locale: Locales.IT, name: translate(HeaderEnum['locales.italian']) },
  ];

  return (
    <nav className="tw-relative tw-w-full" aria-label="Language Selection">
      <DefaultButton
        onClick={() => {
          setLanguageDropdownOpen(!languageDropdownOpen);
          if (!languageDropdownOpen) {
            // If opening dropdown, start timer
            startCloseTimer();
          } else {
            // If closing dropdown, cancel timer
            cancelCloseTimer();
          }
        }}
        aria-haspopup="menu"
        aria-expanded={languageDropdownOpen}
        aria-controls="language-dropdown"
        className="tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-whitespace-nowrap tw-rounded-md tw-py-2 tw-pl-4 tw-pr-4 tw-font-normal tw-text-charcoal-blue tw-transition-colors tw-duration-200 hover:tw-bg-gray-100 nine:tw-max-w-[unset]"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <Globe className={'tw-h-5 tw-w-5 tw-stroke-charcoal-blue'} aria-hidden="true" />
          {translate(HeaderEnum['dashboard_menu.main.language'])}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Image
            src={
              languageConstants.localesWithIcons[
                `${(locale as keyof typeof languageConstants.localesWithIcons) || 'en'}`
              ].icon
            }
            alt={`${locale?.toUpperCase() || 'EN'} flag`}
            width={24}
            height={24}
            aria-hidden="true"
          />
          <ChevronRight className="tw-h-5 tw-w-5 tw-stroke-charcoal-blue" aria-hidden="true" />
        </div>
      </DefaultButton>

      <Transition
        show={languageDropdownOpen}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0 tw-scale-95"
        enterTo="tw-opacity-100 tw-scale-100"
        leave="tw-transition tw-ease-in tw-duration-150"
        as={Fragment}
      >
        <div
          id="language-dropdown"
          role="menu"
          aria-orientation="vertical"
          className="tw-absolute tw-top-0 tw-z-[60] tw-mt-2 tw-min-w-40 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-shadow-xl max-2xl:-tw-left-[217px] 2xl:-tw-right-[217px]"
          ref={dropdownRef}
          // Detect when mouse enters and leaves dropdown
          onMouseEnter={cancelCloseTimer}
          onMouseLeave={startCloseTimer}
          // Also detect touch interaction
          onTouchStart={cancelCloseTimer}
          tabIndex={0}
        >
          <ul
            className="w-fit tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0 tw-py-2"
            role="none"
          >
            {languageOptions.map(option => (
              <li
                key={option.locale}
                className="tw-flex tw-items-center tw-justify-start"
                role="none"
              >
                <DefaultButton
                  onClick={() => handleChangeLanguage(option.locale)}
                  disabled={doingLanguageChange}
                  role="menuitem"
                  aria-disabled={doingLanguageChange}
                  className={clsx(
                    'tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-justify-start tw-gap-3 tw-whitespace-nowrap tw-rounded-md tw-py-2 tw-pl-4 tw-pr-24 tw-font-normal tw-text-charcoal-blue tw-transition-colors tw-duration-200 hover:tw-bg-gray-100',
                    locale === option.locale && 'tw-bg-gray-100'
                  )}
                >
                  {option.name} ({option.locale.toUpperCase()})
                </DefaultButton>
              </li>
            ))}
          </ul>
        </div>
      </Transition>
    </nav>
  );
}
